
import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Home,
  FileText,
  BarChart2,
  Settings,
  Receipt,
  Upload,
  PanelLeftClose,
  PanelLeft,
  ClipboardList,
  Menu,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileMenuDrawer } from "@/components/MobileMenuDrawer";

interface SidebarProps {
  className?: string;
  onNavigate?: () => void;
  isOpen?: boolean;
  onToggle?: () => void;
}

const Sidebar = ({ className, onNavigate, isOpen, onToggle }: SidebarProps) => {
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = useState(isMobile);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useParams();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  
  // Handle sidebar state based on props or internal state
  const sidebarOpen = isOpen !== undefined ? isOpen : !isCollapsed;

  // Effect to collapse sidebar on mobile by default
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  }, [isMobile]);

  const handleNavigate = (path: string) => {
    navigate(path);
    onNavigate?.();
    // Auto-close sidebar on navigation on mobile
    if (isMobile && onToggle) {
      onToggle();
    }
    // Close mobile menu drawer if open
    setIsMobileMenuOpen(false);
  };

  const toggleSidebar = () => {
    if (onToggle) {
      onToggle();
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !organizationId) return;

    setIsUploading(true);
    try {
      const fileName = `${Date.now()}-${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("bank-statements")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase
        .from("bank_statements")
        .insert({
          organization_id: organizationId,
          file_name: file.name,
          file_url: uploadData.path,
          statement_date: new Date().toISOString(),
        });

      if (dbError) throw dbError;

      toast({
        title: "Success",
        description: "Bank statement uploaded successfully",
      });
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Error",
        description: "Failed to upload bank statement",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const menuItems = [
    { 
      icon: Home, 
      label: "Home", 
      path: organizationId ? `/app/${organizationId}/dashboard` : "/" 
    },
    { 
      icon: Receipt, 
      label: "Expenses", 
      path: organizationId ? `/app/${organizationId}/expenses` : "/expenses" 
    },
    {
      icon: ClipboardList,
      label: "Expense Reports",
      path: organizationId ? `/app/${organizationId}/expense-reports` : "/expense-reports"
    },
    { 
      icon: FileText, 
      label: "Reports", 
      path: organizationId ? `/app/${organizationId}/reports` : "/reports" 
    },
    { 
      icon: BarChart2, 
      label: "Analytics", 
      path: organizationId ? `/app/${organizationId}/analytics` : "/analytics"
    },
    { 
      icon: Settings, 
      label: "Settings", 
      path: organizationId ? `/app/${organizationId}/settings` : "/settings" 
    },
  ];

  // Mobile menu button - hidden on all sizes but component still exists
  const MobileMenuButton = () => {
    if (!isMobile) return null;
    
    return (
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 md:hidden invisible" // Added invisible class to hide but not remove
        onClick={() => setIsMobileMenuOpen(true)}
      >
        <Menu className="h-6 w-6" />
      </Button>
    );
  };
  
  // Don't render sidebar if it's closed on mobile
  if (isMobile && !sidebarOpen) {
    return (
      <>
        <MobileMenuButton />
        <MobileMenuDrawer 
          open={isMobileMenuOpen} 
          onOpenChange={setIsMobileMenuOpen}
          menuItems={menuItems}
          onNavigate={handleNavigate}
          organizationId={organizationId}
          handleFileUpload={handleFileUpload}
          isUploading={isUploading}
        />
      </>
    );
  }

  // On mobile, apply padding to the bottom for the bottom navigation
  const sidebarStyle = isMobile ? { paddingBottom: '64px' } : {};

  return (
    <>
      {isMobile && <MobileMenuButton />}
      <div
        className={cn(
          "h-screen bg-white transition-all duration-300 ease-in-out z-30",
          sidebarOpen ? "w-[240px]" : "w-[60px]",
          "flex flex-col border-r",
          isMobile && sidebarOpen ? "fixed left-0 top-0" : "",
          className
        )}
        style={sidebarStyle}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="absolute -right-4 top-4 z-50 h-8 w-8 rounded-full border bg-background shadow-sm hover:bg-accent hover:text-accent-foreground"
        >
          {sidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
        </Button>
        
        <div className="space-y-4 py-4">
          <div className="px-3 py-2">
            <h2 className={cn(
              "text-lg font-semibold text-[#1e293b]",
              !sidebarOpen && "hidden"
            )}>
              My View
            </h2>
          </div>
          <div className="space-y-1 px-3">
            {menuItems.map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-4",
                  "text-[15px] font-medium",
                  location.pathname === item.path 
                    ? "bg-[#3b82f6] text-white hover:bg-[#3b82f6]/90" 
                    : "text-[#1e293b] hover:bg-slate-100"
                )}
                onClick={() => handleNavigate(item.path)}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className={cn("flex-1 text-left", !sidebarOpen && "hidden")}>
                  {item.label}
                </span>
              </Button>
            ))}
            
            {/* Upload Statement Button */}
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start gap-4",
                "text-[15px] font-medium",
                "text-[#1e293b] hover:bg-slate-100"
              )}
              disabled={isUploading}
            >
              <Upload className="h-5 w-5 flex-shrink-0" />
              <label className={cn("flex-1 text-left cursor-pointer", !sidebarOpen && "hidden")}>
                {isUploading ? "Uploading..." : "Upload Statement"}
                <input
                  type="file"
                  className="hidden"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                />
              </label>
            </Button>
          </div>
        </div>
      </div>
      
      <MobileMenuDrawer 
        open={isMobileMenuOpen} 
        onOpenChange={setIsMobileMenuOpen}
        menuItems={menuItems}
        onNavigate={handleNavigate}
        organizationId={organizationId}
        handleFileUpload={handleFileUpload}
        isUploading={isUploading}
      />
    </>
  );
};

export default Sidebar;
